import type { Application } from "@hotwired/stimulus";
import { Expandable, ExpandableSettings } from "./expandable";
import { ExpandableController, type ExpansionStrategy } from "./expandable-controller";

export class RowExpansionStrategy implements ExpansionStrategy {
    constructor(
        private readonly app: Application,
        private readonly controllerIdentifier: string,
    ) {}

    collapse(content: Expandable): void {
        this.hideDescendants(content.element);
        content.expanded = false;
    }

    expand(content: Expandable): void {
        content.expanded = true;
        this.showDirectChildren(content.element);
    }

    getLoadingElement(content: Expandable): HTMLElement {
        return content.element;
    }

    applyLoadedContent(content: Expandable, html: string): void {
        content.element.insertAdjacentHTML("afterend", html);
    }

    private showDirectChildren(row: HTMLElement): void {
        for (const child of this.findChildRows(row)) {
            child.hidden = false;
        }
    }

    private hideDescendants(row: HTMLElement): void {
        for (const child of this.findChildRows(row)) {
            // Collapse any expandables that are nested under this one.
            const childController = this.getRowController(child);
            childController?.collapse();
            child.hidden = true;
        }
    }

    private getRowController(row: HTMLElement): ExpandableController | null {
        return this.app.getControllerForElementAndIdentifier(row, this.controllerIdentifier) as ExpandableController | null;
    }

    private findChildRows(parentRow: HTMLElement): HTMLElement[] {
        // If future usages need a different search scope, we can add an optional server-provided container selector.
        const parentId = parentRow.id;
        const container = parentRow.parentElement!;
        return Array.from(container.querySelectorAll<HTMLElement>(`tr[data-parent="${parentId}"]`));
    }
}
